/**
 * The surfaces `/ai-insights` is asked for and the server does not offer.
 *
 * Task 45 found four of these, Task 53 found seven, Task 48 found seven more —
 * including `GET /audit-logs`, published in both binding documents and mounted
 * nowhere. The answer each time was the same and it is the answer here: **name the
 * missing endpoint on screen, never mock one.**
 *
 * This is data rather than prose in a component so that a test can check it, and the
 * check runs in **both directions** (`availability.test.ts`):
 *
 *  - every path in `ABSENT_AI_SURFACES.needed` is genuinely absent from
 *    `openapi.yaml`, so a panel does not go on apologising for something somebody
 *    landed last week — the failure mode Task 53's incident-timeline note actually
 *    had, and G19 had to delete;
 *  - every path in `PRESENT_AI_PATHS` **is** in `openapi.yaml`, so the fixture this
 *    screen is built against is the published contract and not a transcription of it.
 *
 * A one-directional guard here would be the "scoped to a fact that stopped being
 * true" mistake the standing constraints describe.
 */

/** The four AI paths this screen actually calls. All four are mounted today. */
export const PRESENT_AI_PATHS: readonly string[] = [
  '/api/v1/ai/insights',
  '/api/v1/ai/analyze',
  '/api/v1/ai/chat',
  '/api/v1/ai/insights/{id}/evidence',
];

export interface AbsentSurface {
  /** Stable id — also the test id suffix on screen, so a reader can cite one. */
  readonly id: string;
  /**
   * The path that would close it, exactly as it would be written in `openapi.yaml`,
   * or `null` where the gap is not a path at all (streaming, persistence).
   */
  readonly needed: string | null;
  /** Who has to close it. "I looked and found none" is a finished sentence; this is not. */
  readonly owner: string;
}

/**
 * Seven, and the first is the one that matters most on this screen.
 *
 * `wp_ai_budget` is the table that decides whether a question gets answered at all,
 * and no endpoint reads it. So a reader cannot be shown how much of the month is
 * left; they find out at the moment of refusal, from the `429`'s own three numbers.
 * That is not a rendering choice this screen made — it is the whole of what the
 * contract publishes about a company's AI spend.
 *
 * The directory gap this list used to carry — `needed: '/api/v1/employees'` — closed
 * when Task T2-A shipped `GET /employees`, `/employees/{employeeId}` and
 * `/employees/departments`; do not re-add it.
 */
export const ABSENT_AI_SURFACES: readonly AbsentSurface[] = [
  { id: 'budget', needed: '/api/v1/ai/budget', owner: 'WP-M1 (src/clients/litellm.ts)' },
  { id: 'insightById', needed: '/api/v1/ai/insights/{id}', owner: 'WP-M3 (src/modules/ai/)' },
  { id: 'alertById', needed: '/api/v1/alerts/{id}', owner: 'WP-L1 (src/modules/alerts/)' },
  {
    id: 'dailyById',
    needed: '/api/v1/activities/daily-aggregates/{id}',
    owner: 'WP-J1 (src/modules/activities/)',
  },
  { id: 'jobStatus', needed: '/api/v1/jobs/{id}', owner: 'WP-A (src/jobs/)' },
  { id: 'conversation', needed: null, owner: 'WP-M3 — no conversation table exists, by design' },
  { id: 'streaming', needed: null, owner: 'WP-M3 — 03 §8 asks for it; the contract has no stream' },
];
